/**
 * Arabic messages for the launch screens converted in C7-REST (see
 * ./screens.en.ts). Same keys; plurals carry all six Arabic forms.
 *
 * Terms are the glossary's (docs/help/glossary.md) and page titles match the
 * navigation's Arabic (`nav.*` in ./ar.ts), so a screen is called the same
 * thing in the sidebar and on the page. Every string here that is not a
 * glossary term word for word was written by engineering in the glossary's
 * terms and is listed in SCREENS_AR_DRAFT_KEYS (below) for ux-writer review;
 * ./ar.ts adds that list to AR_DRAFT_KEYS.
 *
 * Identifiers a person types or the system generates (codes, IANA zones,
 * `VehicleInspection`) stay in Latin letters, isolated left to right by the
 * screen; Arabic sentences never contain an English word otherwise
 * (docs/help/voice.md, "Punctuation and script").
 */
import type { Catalog } from "../types";

export const screensAr = {
  fields: {
    name: "الاسم",
    code: "الكود",
    status: "الحالة",
    number: "الرقم",
    date: "التاريخ",
    type: "النوع",
    currency: "العملة",
    customer: "العميل",
    supplier: "المورد",
    product: "الصنف",
    warehouse: "المخزن",
    sku: "كود الصنف",
    quantity: "الكمية",
    qty: "الكمية",
    total: "الإجمالي",
    amount: "المبلغ",
    period: "الفترة",
    actions: "الإجراءات",
    description: "الوصف",
    source: "المصدر",
    reason: "السبب",
    requestedBy: "مقدّم الطلب",
    requested: "تاريخ الطلب",
    received: "تاريخ الاستلام",
    version: "الإصدار",
    title: "العنوان",
    account: "الحساب",
    debit: "مدين",
    credit: "دائن",
    lot: "رقم التشغيلة",
    email: "البريد الإلكتروني",
    company: "الشركة",
    branch: "الفرع",
    legalEntity: "الكيان القانوني",
    address: "العنوان",
    timezone: "المنطقة الزمنية",
    country: "الدولة",
    created: "تاريخ الإنشاء",
    risk: "درجة المخاطرة",
    category: "الفئة",
    module: "الوحدة",
    entity: "نوع السجل",
    event: "الحدث",
    severity: "درجة الخطورة",
    outcome: "النتيجة",
    ip: "عنوان الشبكة",
    device: "الجهاز",
    lastActive: "آخر نشاط",
    from: "من",
    to: "إلى",
    active: "نشط",
    balance: "الرصيد",
    taxId: "رقم التسجيل الضريبي",
    /** PAY-ALLOC: what a payment still holds on account. */
    unallocated: "غير مخصص",
  },
  shared: {
    listSeparator: "، ",
    yes: "نعم",
    no: "لا",
    export: "تصدير",
    previous: "السابق",
    next: "التالي",
    utc: "التوقيت العالمي",
    versionNumber: "الإصدار {{version}}",
  },
  values: {
    accountType: {
      Asset: "أصول",
      Liability: "خصوم",
      Equity: "حقوق الملكية",
      Revenue: "إيرادات",
      Expense: "مصروفات",
    },
    normalBalance: {
      Debit: "مدين",
      Credit: "دائن",
    },
    productType: {
      Item: "صنف مخزني",
      Service: "خدمة",
      Expense: "مصروف",
    },
    customerType: {
      Individual: "فرد",
      Company: "شركة",
    },
    movementType: {
      Receipt: "الإضافة",
      Issue: "الصرف",
      Transfer: "التحويل المخزني",
      Adjustment: "التسوية المخزنية",
      Reservation: "حجز المخزون",
      CycleCount: "الجرد",
      Scrap: "الهالك",
    },
    valuationMethod: {
      Fifo: "الوارد أولاً يُصرف أولاً",
      MovingAverage: "المتوسط المتحرك",
      WeightedAverage: "المتوسط المرجّح",
      Standard: "التكلفة المعيارية",
    },
    taxType: {
      VAT: "ضريبة القيمة المضافة",
      SalesTax: "ضريبة المبيعات",
      WithholdingTax: "ضريبة الخصم والإضافة",
      Excise: "الضريبة الانتقائية",
      Customs: "الرسوم الجمركية",
    },
    risk: {
      Low: "منخفضة",
      Medium: "متوسطة",
      High: "مرتفعة",
      Critical: "حرجة",
    },
    severity: {
      Info: "معلومات",
      Warning: "تحذير",
      Critical: "حرج",
    },
    outcome: {
      Success: "نجح",
      Failure: "فشل",
      Blocked: "مُنِع",
    },
    actorType: {
      User: "مستخدم",
      ApiClient: "تطبيق الربط",
      System: "النظام",
      BreakGlass: "وصول طارئ",
    },
    qualityStatus: {
      Passed: "مطابق",
      Quarantined: "محجوز للفحص",
      Failed: "غير مطابق",
    },
    storageType: {
      Relational: "جداول علائقية",
      Jsonb: "تخزين مرن",
    },
    viewType: {
      List: "قائمة",
      Kanban: "لوحة بطاقات",
      Calendar: "تقويم",
      Pivot: "جدول محوري",
      Tree: "شجرة",
      Map: "خريطة",
    },
    layoutType: {
      Form: "نموذج",
      Wizard: "معالج خطوات",
      Compact: "مختصر",
    },
    fieldType: {
      Text: "نص",
      LongText: "نص طويل",
      RichText: "نص منسَّق",
      Number: "رقم",
      Currency: "مبلغ",
      Percentage: "نسبة مئوية",
      Date: "تاريخ",
      DateTime: "تاريخ ووقت",
      Boolean: "نعم أو لا",
      Select: "اختيار من قائمة",
      MultiSelect: "اختيار متعدد",
      Relation: "ربط بسجل",
      User: "مستخدم",
      Employee: "موظف",
      Company: "شركة",
      Attachment: "مرفق",
      Formula:
        "معادلة (تُحلَّل بأمان، فلا تنفّذ برمجيات ولا تقرأ شيئاً من خارج السجل)",
      JSON: "بيانات منظَّمة",
    },
    planTier: {
      Starter: "باقة البداية",
      SmallBusiness: "باقة الأعمال الصغيرة",
      Professional: "الباقة الاحترافية",
      Enterprise: "باقة المؤسسات",
      EnterprisePlus: "باقة المؤسسات المتقدمة",
    },
    isolationMode: {
      SharedSchema: "مخطط بيانات مشترك",
      DedicatedSchema: "مخطط بيانات مخصّص",
      DedicatedCluster: "خادم قاعدة بيانات مخصّص",
    },
    environment: {
      Sandbox: "بيئة تجريبية",
      Production: "بيئة التشغيل الفعلي",
    },
    systemRole: {
      administrator: "مسؤول النظام",
      auditor: "المراجع",
      credit_controller: "مسؤول الائتمان",
      employee: "موظف",
      finance_manager: "المدير المالي",
      financial_controller: "المراقب المالي",
      governance_officer: "مسؤول الحوكمة",
      hr_manager: "مدير الموارد البشرية",
      manufacturing_manager: "مدير التصنيع",
      owner: "المالك",
      payables_approver: "معتمِد فواتير الموردين",
      project_manager: "مدير المشروعات",
      sales_manager: "مدير المبيعات",
      supply_chain_manager: "مدير سلسلة الإمداد",
    },
  },

  // ---- المؤسسة ----------------------------------------------------------------
  org: {
    section: "المؤسسة",
    list: {
      codeHint: "معرّف قصير يظهر في المستندات والتقارير.",
    },
    structure: {
      title: "الهيكل التنظيمي",
      subtitle:
        "الكيانات القانونية والشركات التابعة لها، وما تحتها من فروع وإدارات ومواقع.",
      manage: "إدارة الكيانات القانونية",
      emptyTitle: "لا يوجد كيان قانوني بعد",
      emptyMessage:
        "الكيان القانوني هو قمة الهيكل: يملك الشركات التي تتبعها الفروع، وعليه تُسجَّل قيود اليومية والسنوات المالية. يُنشأ أول كيان عند تجهيز مساحة العمل، ويمكن إضافة كيان لكل شركة مسجّلة في المجموعة.",
      add: "إضافة كيان قانوني",
      hierarchy: "الهيكل",
      noCompany: "لا توجد شركة تابعة لهذا الكيان بعد.",
      departments_zero: "لا توجد إدارات",
      departments_one: "إدارة واحدة",
      departments_two: "إدارتان",
      departments_few: "{{count}} إدارات",
      departments_many: "{{count}} إدارة",
      departments_other: "{{count}} إدارة",
      branches_zero: "لا توجد فروع",
      branches_one: "فرع واحد",
      branches_two: "فرعان",
      branches_few: "{{count}} فروع",
      branches_many: "{{count}} فرعاً",
      branches_other: "{{count}} فرع",
      locations_zero: "لا توجد مواقع",
      locations_one: "موقع واحد",
      locations_two: "موقعان",
      locations_few: "{{count}} مواقع",
      locations_many: "{{count}} موقعاً",
      locations_other: "{{count}} موقع",
      orphansTitle: "شركات لا يظهر الكيان القانوني التابعة له",
      orphansBody:
        "تشير هذه الشركات إلى كيان قانوني غير ظاهر. تُعرض هنا بدلاً من حذفها من الهيكل، لأن الشركة التي لا تتبع أي كيان لا يمكن الترحيل إليها.",
    },
    legalEntities: {
      title: "الكيانات القانونية",
      subtitle:
        "الشركات المسجّلة في المجموعة. لكل كيان دفاتره وتقويمه المالي ودليل حساباته.",
      new: "كيان قانوني جديد",
      nameHint: "الاسم الذي يظهر به في كل الشاشات.",
      legalName: "الاسم القانوني المسجّل",
      legalNameHint: "إن تُرك فارغاً، يُستخدم الاسم أعلاه.",
      country: "دولة التسجيل",
      currency: "العملة الوظيفية",
      currencyHint:
        "العملة التي يمسك بها هذا الكيان دفاتره، ولا يمكن تغييرها بعد ترحيل أي قيد.",
      taxId: "رقم التسجيل الضريبي",
      creating: "جارٍ الإنشاء…",
      create: "إنشاء الكيان القانوني",
      loadFailed: "تعذّر تحميل الكيانات القانونية",
      emptyTitle: "لا توجد كيانات قانونية",
      emptyMessage:
        "يُنشأ أول كيان قانوني من دولة مساحة العمل عند تجهيزها. أضِف كياناً لكل شركة مسجّلة في المجموعة.",
      registeredName: "الاسم المسجّل",
    },
    legalEntity: {
      notFound: "تعذّر العثور على الكيان القانوني",
      archived: "ربما نُقل إلى الأرشيف.",
      registeredAs: "مسجّل باسم {{name}}",
      deactivate: "إيقاف الكيان",
      deactivating: "جارٍ الإيقاف…",
      activate: "تفعيل الكيان",
      activating: "جارٍ التفعيل…",
      confirmDeactivate:
        "هل تريد إيقاف {{name}}؟\n\nلا يمكن ترحيل أي قيد يومية أو فاتورة أو حركة مخزنية إلى هذا الكيان ما دام موقوفاً. ولا تتأثر السجلات الموجودة.",
      overview: "نظرة عامة",
      companiesTab: "الشركات ({{count}})",
      registration: "بيانات التسجيل",
      taxRegistration: "التسجيل الضريبي",
      companiesTitle: "الشركات التابعة لهذا الكيان",
      noCompanies: "لا توجد شركات",
      noCompaniesMessage:
        "الشركة هي وحدة التشغيل التابعة للكيان القانوني، وإليها تتبع الفروع والإدارات ومستندات العمل اليومية.",
      addCompany: "إضافة شركة",
    },
    companies: {
      title: "الشركات",
      subtitle:
        "وحدات التشغيل. تتبع كل شركة كياناً قانونياً واحداً وتأخذ عنه دفاتره وعملته.",
      new: "شركة جديدة",
      emptyTitle: "لا توجد شركات",
      emptyMessage:
        "الشركة هي ما تتبعه الفروع والإدارات والمستندات. تُنشأ شركة عند تجهيز مساحة العمل، ويمكن إضافة غيرها مع نمو المجموعة.",
      missingParent:
        "يجب أن تتبع الشركة كياناً قانونياً، ولا يوجد كيان بعد. أنشئ كياناً قانونياً أولاً، فمنه تأخذ الشركة عملتها ودولتها.",
    },
    company: {
      notFound: "تعذّر العثور على الشركة",
      partOf: "تابعة لـ {{name}}",
      overview: "نظرة عامة",
      branchesTab: "الفروع ({{count}})",
      departmentsTab: "الإدارات ({{count}})",
      details: "البيانات",
      parentMissing: "تعذّر العثور على الكيان القانوني التابعة له",
      singleSite: "تعمل هذه الشركة من موقع واحد.",
      departmentsHint:
        "على الإدارات تُوزَّع التكاليف، وبحسبها توجَّه طلبات الاعتماد.",
    },
    branches: {
      title: "الفروع",
      subtitle:
        "مواقع الشركة. لكل فرع منطقته الزمنية، وبها تُؤرَّخ مستنداته وتُحسب مهل الإنجاز.",
      new: "فرع جديد",
      emptyTitle: "لا توجد فروع",
      emptyMessage:
        "تفصل الفروع التقارير والمخزون حسب الموقع، ولا يحتاج إليها النشاط الذي يعمل من موقع واحد.",
      missingParent: "يتبع الفرع شركةً، ولا توجد شركة بعد. أنشئ شركة أولاً.",
      timezoneHint:
        "اسم المنطقة الزمنية الدولي. إن تُرك فارغاً، يُستخدم التوقيت العالمي.",
    },
    departments: {
      title: "الإدارات",
      subtitle:
        "الهيكل الإداري داخل الشركة. على الإدارات تُوزَّع التكاليف، وبحسبها يُحدَّد إلى من يذهب طلب الاعتماد.",
      new: "إدارة جديدة",
      emptyTitle: "لا توجد إدارات",
      emptyMessage:
        "بالإدارات تُنسب المصروفات والاعتمادات إلى جزء محدد من النشاط.",
      missingParent: "تتبع الإدارة شركةً، ولا توجد شركة بعد. أنشئ شركة أولاً.",
      reportsTo: "تتبع",
      topLevel: "المستوى الأعلى",
    },
    locations: {
      title: "المواقع",
      subtitle: "أماكن محددة داخل الفرع، مثل الطوابق والمكاتب وأرصفة التحميل.",
      new: "موقع جديد",
      emptyTitle: "لا توجد مواقع",
      emptyMessage:
        "تقسّم المواقع الفرع إلى أماكن أصغر. أما أماكن التخزين داخل المخازن فتُدار من المخزون، حيث بيانات المخزون.",
      missingParent: "يقع الموقع داخل فرع، ولا يوجد فرع بعد. أنشئ فرعاً أولاً.",
    },
  },

  // ---- المالية ----------------------------------------------------------------
  finance: {
    closeChecks: {
      allEntriesPosted: "ترحيل كل قيود اليومية",
      supplierInvoicesPosted: "ترحيل فواتير الموردين",
      customerInvoicesPosted: "ترحيل فواتير العملاء",
      noOverdueReceivables: "لا توجد مستحقات متأخرة السداد",
      stillUnposted: "لم يُرحَّل منها بعد: {{count}}",
      awaitingPosting: "بانتظار الترحيل: {{count}}",
      overdue: "متأخرة السداد: {{count}}",
    },
    dashboard: {
      title: "لوحة المؤشرات المالية",
      subtitle: "النقدية والموردون والعملاء ومدى الجاهزية لإقفال الفترة.",
      cashPosition: "رصيد النقدية",
      accountsPayable: "الموردون",
      accountsReceivable: "العملاء",
      fxGainLoss: "فروق العملة",
      recentEntries: "أحدث قيود اليومية",
      narration: "البيان",
      checklist: "قائمة مراجعة إقفال الفترة",
    },
    accounts: {
      title: "دليل الحسابات",
      subtitle: "الحسابات وأنواعها وطبيعة أرصدتها وقابليتها للترحيل.",
      import: "استيراد",
      new: "حساب جديد",
      accountCode: "كود الحساب",
      list: "الحسابات",
      normalBalance: "طبيعة الرصيد",
      postable: "حساب فرعي",
      inactive: "غير نشط",
    },
    ledger: {
      title: "دفتر الأستاذ العام",
      subtitle:
        "سطور دفتر الأستاذ المرحّلة، الأحدث أولاً، حسب الحساب والتاريخ. كل مبلغ بعملة دفاتره.",
      allAccounts: "كل الحسابات",
      chosenAccount: "الحساب المختار",
      accountsNotLoaded:
        "تعذّر تحميل قائمة الحسابات، لذلك لا يمكن الاختيار منها هنا.",
      accountsCutOff:
        "تُعرض هنا أول {{shown}} من أصل {{total}} حساب فقط. افتح الحساب من ميزان المراجعة لترى حساباً غير مدرج.",
      entityNumber: "الكيان القانوني {{number}}",
      list: "حركات دفتر الأستاذ",
      entry: "القيد",
      runningBalanceNotShown:
        "الرصيد المتراكم والرصيد الافتتاحي لا يظهران بعد.",
    },
    trialBalance: {
      title: "ميزان المراجعة",
      subtitle:
        "مجموع المدين والدائن لكل حساب، ورصيده في جانبه، حتى التاريخ الذي تختاره.",
      asOf: "حتى تاريخ",
      asOfHint: "اتركه فارغاً لتشمل كل القيود المرحّلة دون حدٍّ للتاريخ.",
      debits: "المدين",
      credits: "الدائن",
      debitBalance: "رصيد مدين",
      creditBalance: "رصيد دائن",
      totalDebitBalances: "إجمالي الأرصدة المدينة",
      totalCreditBalances: "إجمالي الأرصدة الدائنة",
      openingNotShown: "الأرصدة الافتتاحية وحركة الفترة لا تظهر بعد.",
      list: "ميزان المراجعة",
      balanced: "متوازن",
      outOfBalance: "غير متوازن",
      totalDebits: "إجمالي المدين",
      totalCredits: "إجمالي الدائن",
    },
    statements: {
      title: "القوائم المالية",
      subtitle:
        "قائمة الدخل والميزانية وقائمة التدفقات النقدية مع المقارنة بين الفترات.",
      compare: "مقارنة بفترة أخرى",
      tabs: {
        ProfitAndLoss: "قائمة الدخل",
        BalanceSheet: "الميزانية",
        CashFlow: "قائمة التدفقات النقدية",
      },
      toDate: "حتى تاريخه",
      allDates: "كل التواريخ",
      allDatesNote:
        "تشمل كل قيد مُرحَّل أيًّا كان تاريخه، ومنها القيود المؤرخة بعد اليوم. يصل اختيار الفترة مع محرك القوائم المالية.",
      sections: {
        Asset: "حسابات الأصول",
        Liability: "حسابات الخصوم",
        Equity: "حسابات حقوق الملكية",
        Revenue: "حسابات الإيرادات",
        Expense: "حسابات المصروفات",
      },
    },
    notAvailable: {
      title: "غير متاح بعد",
      cashFlowStatement:
        "لم يُحدَّد بعد أي الحسابات تمثل النقدية، لذلك لا تُعرض قائمة للتدفقات النقدية بدلًا من عرض قائمة خاطئة. تصل قائمة التدفقات النقدية مع محرك القوائم المالية ({{slice}}).",
      cashPosition:
        "لا يُعرض أي رقم قبل أن يكون صحيحًا. يصل مع قائمة التدفقات النقدية في محرك القوائم المالية ({{slice}}).",
      other: "يصل هذا مع {{slice}}.",
    },
    bankRec: {
      title: "التسوية البنكية",
      subtitle: "رفع كشوف الحساب، ومطابقة المدفوعات، وترحيل التسوية.",
      upload: "رفع كشف حساب",
      list: "كشوف الحساب",
      statement: "كشف الحساب",
      bankAccount: "الحساب البنكي",
      matched: "ما طُوبِق",
      unmatchedColumn: "ما لم يُطابَق",
    },
    cash: {
      title: "إدارة النقدية",
      subtitle: "توقع رصيد النقدية، وأرصدة البنوك، والحركات النقدية.",
      forecast: "الرصيد المتوقع",
      bankBalances: "أرصدة البنوك",
      bankAccount: "الحساب البنكي",
      movements: "أحدث الحركات النقدية",
      in: "وارد",
      out: "منصرف",
    },
    assets: {
      title: "الأصول الثابتة",
      subtitle:
        "سجل الأصول مع جدول الإهلاك والاقتناء والاستبعاد والانخفاض في القيمة.",
      new: "أصل جديد",
      list: "الأصول",
      acquired: "تاريخ الاقتناء",
      cost: "التكلفة",
      netBookValue: "صافي القيمة الدفترية",
    },
    tax: {
      title: "لوحة مؤشرات الضرائب",
      subtitle:
        "الضريبة المستحقة الدفع والضريبة المستردة، والإقرارات الضريبية المسجلة لكل فترة.",
      payable: "ضريبة مستحقة الدفع",
      receivable: "ضريبة مستردة",
      returns: "الإقرارات والتقديمات",
      totalNotShown:
        "لا يُعرض هذا الرقم: لا يملأ أي ترحيل بعدُ سجل الضرائب الذي يُقرأ منه، فسيظهر صفرًا مهما كانت الضريبة المرحّلة.",
      returnFiguresNotShown:
        "لا يُعرض تاريخ استحقاق الإقرار ولا مبلغه: لم يُحتسب أيٌّ منهما بعد.",
    },
    periodClose: {
      title: "إقفال الفترة — {{period}}",
      range: "من {{start}} إلى {{end}}",
      reopen: "إعادة فتح الفترة",
      close: "إقفال الفترة",
      checklist: "قائمة مراجعة الإقفال",
      blockedTitle: "لماذا لا يمكن إقفال الفترة؟",
      resolve: "عالِج «{{item}}» قبل إقفال الفترة.",
    },
    rates: {
      title: "أسعار الصرف",
      subtitle: "إدخال يدوي أو استيراد، مع قفل الأسعار لفترات التقارير.",
      revalue: "تشغيل إعادة التقييم",
      list: "الأسعار",
      rate: "السعر",
      effectiveFrom: "يسري من",
      effectiveTo: "يسري حتى",
      locked: "مقفل",
      lockedYes: "🔒 نعم",
    },
    budgets: {
      title: "الموازنات التقديرية",
      subtitle: "إعداد الموازنات وإصداراتها، ومقارنة الموازنة بالفعلي.",
      new: "موازنة جديدة",
      list: "الموازنات",
      fiscalYear: "السنة المالية",
      budget: "الموازنة",
      actual: "الفعلي",
    },
    consolidation: {
      title: "المعاملات بين الشركات وتوحيد القوائم المالية",
      subtitle:
        "قواعد الربط والاستبعادات وترجمة العملات وتشغيلات توحيد القوائم المالية.",
      run: "تشغيل التوحيد",
      openItems: "البنود المفتوحة بين الشركات",
      openCount: "مفتوحة: {{count}}",
      counterparty: "الطرف المقابل",
      document: "المستند",
      groups: "مجموعات التوحيد",
      entities: "الكيانات",
      lastRun: "آخر تشغيل",
    },
  },

  // ---- المبيعات ---------------------------------------------------------------
  sales: {
    customers: {
      title: "العملاء",
      subtitle: "بيانات العملاء مع الأرصدة وحدود الائتمان وسجل التعاملات.",
      new: "عميل جديد",
      customerNumber: "رقم العميل",
      list: "العملاء",
      creditLimit: "حد الائتمان",
      openBalance: "الرصيد المستحق",
    },
    deliveryNotes: {
      title: "أذون التسليم",
      subtitle:
        "تجميع الأصناف وتعبئتها وشحنها، ومتابعة الطلبات المؤجلة والمرتجعات.",
      new: "إذن تسليم جديد",
      list: "أذون التسليم",
      order: "أمر البيع",
      lines: "عدد البنود",
    },
    returns: {
      title: "مردودات المبيعات",
      subtitle:
        "طلبات الإرجاع واعتمادها واستلامها، ثم الاستبدال أو الإشعار الدائن.",
      new: "طلب إرجاع جديد",
      list: "المردودات",
      sourceInvoice: "الفاتورة الأصلية",
      refund: "المبلغ المردود",
    },
    priceLists: {
      title: "قوائم الأسعار والخصومات",
      subtitle: "إعداد قوائم الأسعار وقواعد التسعير وحدود اعتماد الخصومات.",
      new: "قائمة أسعار جديدة",
      list: "قوائم الأسعار",
      items: "الأصناف",
      discounts: "قواعد الخصم",
      discount: "الخصم",
      minAmount: "الحد الأدنى للمبلغ",
      approvalThreshold: "حد الاعتماد",
    },
    creditReview: {
      title: "مراجعة حدود الائتمان",
      subtitle: "قرارات الائتمان المعلّقة مع درجة مخاطرة العميل ومديونيته.",
      approveSelected: "اعتماد المحدد",
      list: "القرارات المعلّقة",
      currentLimit: "الحد الحالي",
      requestedLimit: "الحد المطلوب",
      openAr: "المديونية القائمة",
    },
    commissions: {
      title: "لوحة العمولات",
      subtitle: "عمولات مندوبي المبيعات وحالة صرفها.",
      list: "العمولات",
      agent: "المندوب",
      earned: "المستحق",
      paid: "المدفوع",
    },
  },

  // ---- المشتريات --------------------------------------------------------------
  procurement: {
    requests: {
      title: "طلبات الشراء",
      subtitle:
        "طلبات داخلية لشراء سلع أو خدمات، يعتمدها صاحب الموازنة، ثم تُحوَّل إلى أمر شراء.",
      new: "طلب شراء جديد",
      list: "طلبات الشراء",
      department: "الإدارة",
      expected: "التاريخ المتوقع",
    },
    rfqs: {
      title: "طلبات عروض الأسعار",
      subtitle: "طلب عروض أسعار من الموردين، وجمع العروض، وترسية الطلب.",
      new: "طلب عرض أسعار جديد",
      list: "طلبات عروض الأسعار",
      suppliers: "الموردون",
      quotes: "العروض",
      issued: "تاريخ الإصدار",
      deadline: "آخر موعد للرد",
    },
    suppliers: {
      title: "الموردون",
      subtitle:
        "بيانات الموردين: رقم التسجيل الضريبي والبنك والحالة والأداء والعقود.",
      new: "مورد جديد",
      supplierNumber: "رقم المورد",
      list: "الموردون",
      terms: "شروط السداد",
      openPayables: "المستحق للمورد",
    },
    evaluations: {
      title: "تقييم الموردين",
      subtitle:
        "بطاقات الأداء: الالتزام بمواعيد التسليم والجودة وفروق الأسعار والمخاطر.",
      list: "التقييمات",
      onTime: "الالتزام بالمواعيد",
      quality: "الجودة",
      priceVariance: "فروق الأسعار",
      overall: "التقييم العام",
    },
    contracts: {
      title: "عقود الشراء",
      subtitle: "شروط العقود ومواعيد تجديدها وأوامر الشراء المرتبطة بها.",
      new: "عقد جديد",
      list: "العقود",
      start: "تاريخ البدء",
      end: "تاريخ الانتهاء",
      linkedPos: "أوامر الشراء المرتبطة",
    },
  },

  // ---- المخزون ----------------------------------------------------------------
  inventory: {
    products: {
      title: "الأصناف",
      subtitle:
        "بيانات الأصناف: المتغيرات ووحدة القياس وطريقة التقييم والضريبة والحالة والباركود.",
      new: "صنف جديد",
      list: "الأصناف",
      unitOfMeasure: "وحدة القياس",
      uom: "وحدة القياس",
      valuation: "طريقة التقييم",
      barcode: "الباركود",
      variants: "المتغيرات",
      typeOptions: {
        Item: "صنف مخزني — بضاعة تُخزَّن",
        Service: "خدمة — دون مخزون",
        Expense: "مصروف",
      },
    },
    stock: {
      title: "المخزون حسب الموقع",
      subtitle:
        "الرصيد الفعلي والكمية المحجوزة والكمية المتاحة حسب المخزن ورقم التشغيلة.",
      allWarehouses: "كل المخازن",
      list: "الأرصدة",
      onHand: "الرصيد الفعلي",
      reserved: "الكمية المحجوزة",
      available: "الكمية المتاحة",
    },
    movements: {
      title: "الحركات المخزنية",
      subtitle:
        "سجل حركات لا يُعدَّل: الإضافة والصرف والتحويل المخزني والتسوية المخزنية والهالك.",
      new: "حركة جديدة",
      list: "الحركات",
      value: "القيمة",
      posted: "تاريخ الترحيل",
    },
    transfers: {
      title: "التحويلات المخزنية",
      subtitle:
        "تحويل المخزون بين المخازن مع الشحن والاستلام ومتابعة ما في الطريق.",
      new: "تحويل جديد",
      list: "التحويلات",
      shipped: "تاريخ الشحن",
    },
    adjustments: {
      title: "التسويات المخزنية",
      subtitle: "تسويات المخزون مع السبب والاعتماد وترحيل لا يُعدَّل.",
      new: "تسوية جديدة",
      awaiting: "تسويات بانتظار الاعتماد",
      exceeds: "تتجاوز التسوية {{number}} الحد المحدد لتسويات المخزون.",
      review: "راجِع التسوية ثم اعتمدها أو ارفضها من قائمة الاعتمادات.",
      list: "التسويات",
      delta: "الفرق",
    },
    cycleCounts: {
      title: "الجرد الدوري",
      subtitle: "كشوف الجرد وتسجيل الفروق واعتمادها وترحيلها.",
      start: "بدء الجرد",
      list: "عمليات الجرد",
      system: "رصيد النظام",
      counted: "الكمية المجرودة",
      variance: "الفرق",
      countedBy: "القائم بالجرد",
    },
    traceability: {
      title: "تتبع التشغيلات والأرقام التسلسلية",
      subtitle: "أصل كل تشغيلة ورقم تسلسلي: المصادر والحركات وحالة الجودة.",
      searchPlaceholder: "ابحث برقم التشغيلة أو الرقم التسلسلي…",
      searchLabel: "البحث في التتبع",
      list: "سجلات التتبع",
      serial: "الرقم التسلسلي",
      quality: "الجودة",
    },
    reorder: {
      title: "مخطط إعادة الطلب",
      subtitle:
        "اقتراحات إعادة التوريد حسب الحدين الأدنى والأقصى، مع أوامر الشراء المخططة.",
      generate: "إنشاء أوامر الشراء",
      belowTitle: "أصناف تحت الحد الأدنى بلا أمر شراء مخطط",
      below:
        "الصنف {{sku}} في {{warehouse}} تحت حده الأدنى (الرصيد الفعلي {{onHand}}، والحد الأدنى {{min}}).",
      belowFix: "أنشئ أوامر شراء مخططة لهذه الأصناف.",
      list: "الاقتراحات",
      onHand: "الرصيد الفعلي",
      min: "الحد الأدنى",
      max: "الحد الأقصى",
      suggested: "الكمية المقترحة",
      plannedPo: "أمر الشراء المخطط",
      exists: "موجود",
      missing: "غير موجود",
    },
    valuation: {
      title: "تقرير تقييم المخزون",
      subtitle: "المخزون المتاح الآن وقيمته، حسب الصنف والمخزن.",
      nowOnly:
        "هذا هو المخزون كما هو الآن. تقييم المخزون في تاريخ سابق غير متاح بعد.",
      totalAllPages: "القيمة الإجمالية لكل الصفحات",
      list: "التقييم",
      method: "طريقة التقييم",
      unitCost: "تكلفة الوحدة",
      totalValue: "القيمة الإجمالية",
    },
  },

  // ---- الإعداد ----------------------------------------------------------------
  admin: {
    wizard: {
      title: "معالج التهيئة",
      subtitle: "يوجّهك النظام، ويتحقق من كل خطوة لحظة بلحظة.",
      empty: "لا توجد مراحل تهيئة متاحة لمساحة العمل هذه بعد.",
      phases: "المراحل",
      step: "{{number}}. {{label}}",
      blocked: "متوقفة",
      phaseTitle: "المرحلة {{number}}: {{label}}",
      whyBlocked: "لماذا هذه المرحلة متوقفة؟",
      phaseNames: {
        Organization: "الهيكل التنظيمي",
        Finance: "الإعداد المالي",
        Tax: "إعداد الضرائب",
        UsersRoles: "المستخدمون والأدوار الوظيفية",
        Approvals: "مسارات الاعتماد",
        MasterData: "البيانات الأساسية",
        OpeningBalances: "الأرصدة الافتتاحية",
        Integrations: "التكاملات",
        Testing: "الاختبار وقبول المستخدمين",
        GoLive: "بدء التشغيل الفعلي",
      },
      notices: {
        SETUP_NO_COMPANY: "لم تُنشأ أي شركة بعد.",
        SETUP_NO_ACCOUNTS: "دليل الحسابات فارغ.",
        SETUP_NO_TAX_CODES: "لم يُعرَّف أي كود ضريبة بعد.",
        SETUP_NO_MEMBERS: "لم يُضَف أي عضو بعد.",
        SETUP_NO_APPROVAL_MATRIX: "لم تُهيَّأ مصفوفة الاعتمادات بعد.",
        SETUP_NO_PRODUCTS: "لم يُضَف أي صنف أو خدمة بعد.",
        SETUP_NO_OPENING_ENTRIES: "لم يُرحَّل أي قيد افتتاحي بعد.",
        SETUP_NO_INTEGRATIONS: "لا توجد تكاملات مهيَّأة. هذه المرحلة اختيارية.",
        SETUP_NO_TEST_TRANSACTIONS: "لم تُسجَّل أي معاملات للاختبار عليها بعد.",
        SETUP_NOT_LIVE: "لم تبدأ مساحة العمل التشغيل الفعلي بعد.",
        SETUP_WAITING_ON: "بانتظار اكتمال مرحلة «{{phase}}».",
        SETUP_PHASE_COMPLETE: "اكتملت مرحلة «{{phase}}».",
      },
    },
    auditLog: {
      title: "سجل المراجعة",
      subtitle:
        "أحداث الأمان والوصول، تُضاف ولا تُعدَّل. لا يملك مسؤولو مساحة العمل تعديلها أو حذفها.",
      eventFilter: "تصفية حسب نوع الحدث",
      allSeverities: "كل درجات الخطورة",
      when: "الوقت",
      actorType: "نوع المنفِّذ",
      target: "الهدف",
      targetId: "معرّف الهدف",
    },
    apiWebhooks: {
      title: "إدارة تطبيقات الربط وإشعارات الويب",
      subtitle:
        "مفاتيح تطبيقات الربط، واشتراكات إشعارات الويب، وسجلات التسليم، والرسائل التي تعذّر تسليمها.",
      newClient: "تطبيق ربط جديد",
      tabs: {
        clients: "تطبيقات الربط",
        webhooks: "إشعارات الويب",
        deadLetter: "رسائل تعذّر تسليمها",
      },
      clientId: "معرّف التطبيق",
      scopes: "نطاقات الصلاحية",
      lastUsed: "آخر استخدام",
      webhooksTitle: "اشتراكات إشعارات الويب",
      endpoint: "عنوان الاستقبال",
      events: "الأحداث",
      paused: "متوقف مؤقتاً",
      lastDelivery: "آخر تسليم",
      failures: "مرات الفشل",
      deadLetterTitle: "رسائل تعذّر تسليمها",
      topic: "الموضوع",
      eventId: "معرّف الحدث",
      attempts: "المحاولات",
      nextAttempt: "المحاولة التالية",
    },
    users: {
      title: "إدارة المستخدمين",
      subtitle:
        "دعوة المستخدمين وإسناد الأدوار الوظيفية وإدارة التحقق بخطوتين والجلسات.",
      invite: "دعوة مستخدم",
      list: "المستخدمون",
      mfa: "التحقق بخطوتين",
      on: "مفعّل",
      off: "غير مفعّل",
      roles: "الأدوار الوظيفية",
      lastLogin: "آخر تسجيل دخول",
      cancelInvitation: "إلغاء الدعوة",
      suspend: "إيقاف",
      activate: "تفعيل",
    },
    roles: {
      title: "الأدوار الوظيفية والصلاحيات",
      subtitle:
        "تعريف الأدوار الوظيفية ومصفوفة الصلاحيات ونطاقات الصلاحية والصلاحيات على مستوى الحقول.",
      create: "إنشاء دور وظيفي",
      list: "الأدوار الوظيفية",
      role: "الدور الوظيفي",
      system: "من النظام",
      custom: "مخصّص",
      permissions: "الصلاحيات",
      members: "الأعضاء",
      sodConflicts: "تعارضات الفصل بين المهام",
    },
    workflows: {
      title: "منشئ مسارات العمل",
      subtitle:
        "تصميم مراحل مسار العمل بشروطها واعتماداتها ومهل إنجازها وإصداراتها.",
      validate: "تحقق",
      simulate: "محاكاة",
      publish: "نشر الإصدار",
      states: "المراحل",
      active: "نشط",
      draft: "مسودة",
      versionBadge: "{{state}}، الإصدار {{version}}",
      initial: "البداية",
      final: "النهاية",
      transitions: "الانتقالات",
      auto: "تلقائي",
    },
    approvalMatrix: {
      title: "مصمم مصفوفة الاعتمادات",
      subtitle: "ضبط حدود الاعتماد والأدوار المعتمِدة والتفويض والتصعيد.",
      validate: "تحقق",
      activate: "تفعيل الإصدار",
      unnamed: "مصفوفة اعتمادات جديدة",
      entity: "نوع السجل: {{entity}}",
      roleStep: "الدور الوظيفي: {{role}}",
      counterSignatures: "عدد التوقيعات الإضافية المطلوبة: {{count}}",
      escalation: "قواعد التصعيد",
      afterHours: "بعد (ساعات)",
      escalateTo: "التصعيد إلى",
      slaBreach: "تجاوز مهلة الإنجاز",
    },
    entityBuilder: {
      title: "منشئ نماذج البيانات",
      subtitle:
        "أنشئ نماذج بيانات مخصّصة بحقولها وخياراتها وقواعد التحقق منها وتفعيلها، دون برمجة ودون تعديل بنية قاعدة البيانات.",
      validateActivate: "التحقق والتفعيل",
      newEntity: "نموذج بيانات مخصّص جديد",
      entityName: "اسم النموذج",
      entityNameHint: "مثال: {{example}}",
      whyBlocked: "لماذا هذا ممنوع؟",
      invalidName:
        "يبدأ اسم النموذج بحرف لاتيني، ولا يحتوي إلا على حروف لاتينية وأرقام وشرطة سفلية.",
      reservedName:
        "الاسم «{{name}}» محجوز للنظام، ولا يمكن استخدامه لنموذج بيانات مخصّص.",
      fieldTypes: "أنواع الحقول",
      list: "نماذج البيانات المخصّصة",
      storage: "طريقة التخزين",
      fields: "الحقول",
    },
    formDesigner: {
      title: "مصمم النماذج وطرق العرض",
      subtitle:
        "ترتيب أقسام النماذج وتبويباتها، وإظهار الحقول بشروط، وطرق العرض كالقائمة ولوحة البطاقات والتقويم.",
      tabs: {
        views: "طرق العرض",
        layouts: "تخطيطات النماذج",
      },
      viewsTitle: "تعريفات طرق العرض",
      view: "طريقة العرض",
      default: "الافتراضية",
      layoutsTitle: "تخطيطات النماذج",
      layout: "التخطيط",
    },
    countryPacks: {
      title: "إدارة حزم الدول",
      subtitle:
        "تضبط حزمة الدولة الضرائب والتقويم والصيغ والتقارير القانونية والفاتورة الإلكترونية لكل دولة.",
      list: "حزم الدول",
      pack: "حزمة الدولة",
      packName: "حزمة {{country}}",
      installed: "المثبَّت",
      latest: "الأحدث",
      update: "التحديث",
      available: "متاح",
      current: "محدَّثة",
      install: "تثبيت",
      upgrade: "تحديث",
      disable: "تعطيل",
      review: "المراجعة",
      reviewState: {
        Default: "إعداد افتراضي - يُنصح بمراجعته",
        Certified: "معتمدة",
      },
      reviewUnknown: "غير محددة",
      explain: {
        title: "{{pack}}: معنى هذا الوصف",
        status: "الحالة",
        Default:
          "إعداد افتراضي - يُنصح بمراجعته. يطبّق {{product}} هذه الإعدادات الافتراضية المبنية على المعايير لتبدأ مساحة العمل عملها، ولم يراجعها محاسب مرخَّص.",
        Certified:
          "مُعلَّمة بأنها معتمدة، ولم يُسجَّل لها مراجِع ولا تاريخ مراجعة.",
        reviewer: "المراجِع",
        notReviewed: "لم يراجعها محاسب مرخَّص",
        date: "التاريخ",
        notRecorded: "غير مسجَّل",
        sources: "المصدر",
        noSources: "لا يذكر محتوى هذه الحزمة أي مصدر.",
        responsibility: "مسؤولية الإدارة",
        responsibilityText:
          "تتحمّل الإدارة مسؤولية مراجعة هذه الإعدادات الافتراضية، وتغيير ما لا يناسب المنشأة منها، والأرقام التي تعرضها مساحة العمل.",
        close: "إغلاق",
        parts: {
          accounting: "المحاسبة",
          accounting_costing: "طرق التكلفة",
          agingBuckets: "فترات أعمار الديون",
          payrollAccounting: "حسابات الرواتب",
          tax: "أكواد الضريبة",
        },
        words: {
          uncertified: "غير معتمدة",
          certified: "معتمدة",
          "standard-based": "مبنية على المعايير",
        },
      },
    },
    industryPacks: {
      title: "إدارة حزم القطاعات",
      subtitle:
        "ثبّت حزم القطاعات لتحميل نماذج البيانات ومسارات العمل ولوحات المؤشرات وقواعد الترحيل الخاصة بقطاعك.",
      browse: "تصفح سوق الإضافات",
      list: "حزم القطاعات",
      pack: "حزمة القطاع",
      entities: "نماذج البيانات",
      workflows: "مسارات العمل",
      notInstalled: "غير مثبَّتة",
      install: "تثبيت",
      manage: "إدارة",
    },
    integrations: {
      title: "مركز التكامل",
      subtitle:
        "اربط البنوك وبوابات الدفع والتجارة الإلكترونية ومصلحة الضرائب والفاتورة الإلكترونية وغيرها.",
      add: "إضافة موصل",
      list: "الموصلات",
      connector: "الموصل",
      successRate: "نسبة النجاح",
      lastRun: "آخر تشغيل",
      configure: "تهيئة",
      settings: "الإعدادات",
    },
    dataImport: {
      title: "مركز استيراد البيانات",
      subtitle:
        "استيراد البيانات الأساسية والأرصدة الافتتاحية مع ربط الأعمدة والتحقق والتشغيل التجريبي والمطابقة.",
      template: "تنزيل النموذج",
      new: "استيراد جديد",
      list: "سجل الاستيراد",
      rows: "الصفوف",
      success: "الناجحة",
      errors: "الأخطاء",
      started: "تاريخ البدء",
      open: "فتح",
    },
    tenant: {
      title: "إعدادات مساحة العمل",
      subtitle: "بيانات الشركة والإعدادات المحلية وتصدير البيانات.",
      save: "حفظ التغييرات",
      environment: "البيئة: {{value}}",
      status: "الحالة: {{value}}",
      plan: "الباقة: {{value}}",
      company: "الشركة",
      companyName: "اسم الشركة",
      tenantCode: "كود مساحة العمل",
      localization: "الإعدادات المحلية",
      locale: "اللغة والمنطقة",
      region: "منطقة حفظ البيانات",
      isolation: "طريقة عزل البيانات",
      backup: "تصدير البيانات",
      exportAll: "تصدير كل البيانات",
    },
    security: {
      title: "مركز الأمان",
      subtitle:
        "سجل المراجعة والجلسات النشطة ومفاتيح تطبيقات الربط وتنبيهات النشاط غير المعتاد.",
      tabs: {
        audit: "سجل المراجعة",
        sessions: "الجلسات",
      },
      events: "أحداث الأمان",
      time: "الوقت",
      sessionsTitle: "الجلسات النشطة",
      revokeAll: "إنهاء كل الجلسات",
      thisSession: "هذه الجلسة",
      revoke: "إنهاء الجلسة",
    },
    billing: {
      title: "الفوترة والاشتراك",
      subtitle: "الباقة والاستخدام والفواتير ووسيلة الدفع.",
      compare: "مقارنة الباقات",
      plan: "الباقة",
      users: "المستخدمون",
      apiRequests: "طلبات تطبيقات الربط",
      paymentMethod: "وسيلة الدفع",
      none: "لا توجد وسيلة دفع مسجّلة",
      invoices: "الفواتير",
    },
  },
} satisfies Catalog;

/**
 * The strings above that are not glossary terms word for word, by key (a
 * plural by its base key): engineering's drafts, in the glossary's terms,
 * for ux-writer to review. Approve one by deleting its line. Not listed,
 * because each is a glossary term (docs/help/glossary.md) exactly: the column
 * names for code, number, customer, supplier, item, warehouse, item code,
 * account, debit, credit, lot, company, branch, legal entity, tax
 * registration number; the glossary's words for stock movements, valuation
 * methods, VAT and withholding tax; and screen titles that are glossary terms
 * (دليل الحسابات، ميزان المراجعة، العملاء، الموردون…).
 */
export const SCREENS_AR_DRAFT_KEYS: readonly string[] = [
  // PAY-ALLOC: the payments list's on-account column, for ux-writer review.
  "fields.unallocated",
  "fields.name",
  "fields.status",
  "fields.date",
  "fields.type",
  "fields.currency",
  "fields.quantity",
  "fields.qty",
  "fields.total",
  "fields.amount",
  "fields.period",
  "fields.actions",
  "fields.description",
  "fields.source",
  "fields.reason",
  "fields.requestedBy",
  "fields.requested",
  "fields.received",
  "fields.version",
  "fields.title",
  "fields.email",
  "fields.address",
  "fields.timezone",
  "fields.country",
  "fields.created",
  "fields.risk",
  "fields.category",
  "fields.module",
  "fields.entity",
  "fields.event",
  "fields.severity",
  "fields.outcome",
  "fields.ip",
  "fields.device",
  "fields.lastActive",
  "fields.from",
  "fields.to",
  "fields.balance",
  "shared.listSeparator",
  "shared.yes",
  "shared.no",
  "shared.export",
  "shared.previous",
  "shared.next",
  "shared.utc",
  "shared.versionNumber",
  "values.accountType.Asset",
  "values.accountType.Liability",
  "values.accountType.Equity",
  "values.accountType.Expense",
  "values.productType.Item",
  "values.productType.Service",
  "values.productType.Expense",
  "values.customerType.Individual",
  "values.valuationMethod.MovingAverage",
  "values.valuationMethod.Standard",
  "values.taxType.SalesTax",
  "values.taxType.Excise",
  "values.taxType.Customs",
  "values.risk.Low",
  "values.risk.Medium",
  "values.risk.High",
  "values.risk.Critical",
  "values.severity.Info",
  "values.severity.Warning",
  "values.severity.Critical",
  "values.outcome.Success",
  "values.outcome.Failure",
  "values.outcome.Blocked",
  "values.actorType.User",
  "values.actorType.System",
  "values.actorType.BreakGlass",
  "values.qualityStatus.Passed",
  "values.qualityStatus.Quarantined",
  "values.qualityStatus.Failed",
  "values.storageType.Relational",
  "values.storageType.Jsonb",
  "values.viewType.List",
  "values.viewType.Kanban",
  "values.viewType.Calendar",
  "values.viewType.Pivot",
  "values.viewType.Tree",
  "values.viewType.Map",
  "values.layoutType.Form",
  "values.layoutType.Wizard",
  "values.layoutType.Compact",
  "values.fieldType.Text",
  "values.fieldType.LongText",
  "values.fieldType.RichText",
  "values.fieldType.Number",
  "values.fieldType.Currency",
  "values.fieldType.Percentage",
  "values.fieldType.Date",
  "values.fieldType.DateTime",
  "values.fieldType.Boolean",
  "values.fieldType.Select",
  "values.fieldType.MultiSelect",
  "values.fieldType.Relation",
  "values.fieldType.User",
  "values.fieldType.Employee",
  "values.fieldType.Attachment",
  "values.fieldType.Formula",
  "values.fieldType.JSON",
  "values.planTier.Starter",
  "values.planTier.SmallBusiness",
  "values.planTier.Professional",
  "values.planTier.Enterprise",
  "values.planTier.EnterprisePlus",
  "values.isolationMode.SharedSchema",
  "values.isolationMode.DedicatedSchema",
  "values.isolationMode.DedicatedCluster",
  "values.environment.Sandbox",
  "values.environment.Production",
  "values.systemRole.auditor",
  "values.systemRole.credit_controller",
  "values.systemRole.employee",
  "values.systemRole.finance_manager",
  "values.systemRole.financial_controller",
  "values.systemRole.governance_officer",
  "values.systemRole.hr_manager",
  "values.systemRole.manufacturing_manager",
  "values.systemRole.owner",
  "values.systemRole.payables_approver",
  "values.systemRole.project_manager",
  "values.systemRole.sales_manager",
  "values.systemRole.supply_chain_manager",
  "org.section",
  "org.list.codeHint",
  "org.structure.title",
  "org.structure.subtitle",
  "org.structure.manage",
  "org.structure.emptyTitle",
  "org.structure.emptyMessage",
  "org.structure.add",
  "org.structure.hierarchy",
  "org.structure.noCompany",
  "org.structure.departments",
  "org.structure.branches",
  "org.structure.locations",
  "org.structure.orphansTitle",
  "org.structure.orphansBody",
  "org.legalEntities.title",
  "org.legalEntities.subtitle",
  "org.legalEntities.new",
  "org.legalEntities.nameHint",
  "org.legalEntities.legalName",
  "org.legalEntities.legalNameHint",
  "org.legalEntities.country",
  "org.legalEntities.currency",
  "org.legalEntities.currencyHint",
  "org.legalEntities.creating",
  "org.legalEntities.create",
  "org.legalEntities.loadFailed",
  "org.legalEntities.emptyTitle",
  "org.legalEntities.emptyMessage",
  "org.legalEntities.registeredName",
  "org.legalEntity.notFound",
  "org.legalEntity.archived",
  "org.legalEntity.registeredAs",
  "org.legalEntity.deactivate",
  "org.legalEntity.deactivating",
  "org.legalEntity.activate",
  "org.legalEntity.activating",
  "org.legalEntity.confirmDeactivate",
  "org.legalEntity.overview",
  "org.legalEntity.companiesTab",
  "org.legalEntity.registration",
  "org.legalEntity.taxRegistration",
  "org.legalEntity.companiesTitle",
  "org.legalEntity.noCompanies",
  "org.legalEntity.noCompaniesMessage",
  "org.legalEntity.addCompany",
  "org.companies.title",
  "org.companies.subtitle",
  "org.companies.new",
  "org.companies.emptyTitle",
  "org.companies.emptyMessage",
  "org.companies.missingParent",
  "org.company.notFound",
  "org.company.partOf",
  "org.company.overview",
  "org.company.branchesTab",
  "org.company.departmentsTab",
  "org.company.details",
  "org.company.parentMissing",
  "org.company.singleSite",
  "org.company.departmentsHint",
  "org.branches.title",
  "org.branches.subtitle",
  "org.branches.new",
  "org.branches.emptyTitle",
  "org.branches.emptyMessage",
  "org.branches.missingParent",
  "org.branches.timezoneHint",
  "org.departments.title",
  "org.departments.subtitle",
  "org.departments.new",
  "org.departments.emptyTitle",
  "org.departments.emptyMessage",
  "org.departments.missingParent",
  "org.departments.reportsTo",
  "org.departments.topLevel",
  "org.locations.title",
  "org.locations.subtitle",
  "org.locations.new",
  "org.locations.emptyTitle",
  "org.locations.emptyMessage",
  "org.locations.missingParent",
  "finance.closeChecks.allEntriesPosted",
  "finance.closeChecks.supplierInvoicesPosted",
  "finance.closeChecks.customerInvoicesPosted",
  "finance.closeChecks.noOverdueReceivables",
  "finance.closeChecks.stillUnposted",
  "finance.closeChecks.awaitingPosting",
  "finance.closeChecks.overdue",
  "finance.dashboard.title",
  "finance.dashboard.subtitle",
  "finance.dashboard.cashPosition",
  "finance.dashboard.recentEntries",
  "finance.dashboard.narration",
  "finance.dashboard.checklist",
  "finance.accounts.subtitle",
  "finance.accounts.import",
  "finance.accounts.new",
  "finance.accounts.accountCode",
  "finance.accounts.list",
  "finance.accounts.normalBalance",
  "finance.accounts.inactive",
  "finance.ledger.subtitle",
  "finance.ledger.allAccounts",
  "finance.ledger.chosenAccount",
  "finance.ledger.accountsNotLoaded",
  "finance.ledger.accountsCutOff",
  "finance.ledger.entityNumber",
  "finance.ledger.list",
  "finance.ledger.entry",
  "finance.ledger.runningBalanceNotShown",
  "finance.trialBalance.subtitle",
  "finance.trialBalance.asOf",
  "finance.trialBalance.asOfHint",
  "finance.trialBalance.debits",
  "finance.trialBalance.credits",
  "finance.trialBalance.debitBalance",
  "finance.trialBalance.creditBalance",
  "finance.trialBalance.totalDebitBalances",
  "finance.trialBalance.totalCreditBalances",
  "finance.trialBalance.openingNotShown",
  "finance.trialBalance.balanced",
  "finance.trialBalance.outOfBalance",
  "finance.trialBalance.totalDebits",
  "finance.trialBalance.totalCredits",
  "finance.statements.subtitle",
  "finance.statements.compare",
  "finance.statements.tabs.ProfitAndLoss",
  "finance.statements.tabs.BalanceSheet",
  "finance.statements.tabs.CashFlow",
  "finance.statements.toDate",
  "finance.statements.allDates",
  "finance.statements.allDatesNote",
  "finance.statements.sections.Asset",
  "finance.statements.sections.Liability",
  "finance.statements.sections.Equity",
  "finance.statements.sections.Revenue",
  "finance.statements.sections.Expense",
  "finance.notAvailable.title",
  "finance.notAvailable.cashFlowStatement",
  "finance.notAvailable.cashPosition",
  "finance.notAvailable.other",
  "finance.bankRec.subtitle",
  "finance.bankRec.upload",
  "finance.bankRec.list",
  "finance.bankRec.statement",
  "finance.bankRec.bankAccount",
  "finance.bankRec.matched",
  "finance.bankRec.unmatchedColumn",
  "finance.cash.title",
  "finance.cash.subtitle",
  "finance.cash.forecast",
  "finance.cash.bankBalances",
  "finance.cash.bankAccount",
  "finance.cash.movements",
  "finance.cash.in",
  "finance.cash.out",
  "finance.assets.title",
  "finance.assets.subtitle",
  "finance.assets.new",
  "finance.assets.list",
  "finance.assets.acquired",
  "finance.assets.cost",
  "finance.assets.netBookValue",
  "finance.tax.title",
  "finance.tax.subtitle",
  "finance.tax.payable",
  "finance.tax.receivable",
  "finance.tax.returns",
  "finance.tax.totalNotShown",
  "finance.tax.returnFiguresNotShown",
  "finance.periodClose.title",
  "finance.periodClose.range",
  "finance.periodClose.checklist",
  "finance.periodClose.blockedTitle",
  "finance.periodClose.resolve",
  "finance.rates.title",
  "finance.rates.subtitle",
  "finance.rates.revalue",
  "finance.rates.list",
  "finance.rates.rate",
  "finance.rates.effectiveFrom",
  "finance.rates.effectiveTo",
  "finance.rates.locked",
  "finance.rates.lockedYes",
  "finance.budgets.title",
  "finance.budgets.subtitle",
  "finance.budgets.new",
  "finance.budgets.list",
  "finance.budgets.budget",
  "finance.budgets.actual",
  "finance.consolidation.title",
  "finance.consolidation.subtitle",
  "finance.consolidation.run",
  "finance.consolidation.openItems",
  "finance.consolidation.openCount",
  "finance.consolidation.counterparty",
  "finance.consolidation.document",
  "finance.consolidation.groups",
  "finance.consolidation.entities",
  "finance.consolidation.lastRun",
  "sales.customers.subtitle",
  "sales.customers.new",
  "sales.customers.customerNumber",
  "sales.deliveryNotes.title",
  "sales.deliveryNotes.subtitle",
  "sales.deliveryNotes.new",
  "sales.deliveryNotes.list",
  "sales.deliveryNotes.lines",
  "sales.returns.subtitle",
  "sales.returns.new",
  "sales.returns.list",
  "sales.returns.sourceInvoice",
  "sales.returns.refund",
  "sales.priceLists.title",
  "sales.priceLists.subtitle",
  "sales.priceLists.new",
  "sales.priceLists.list",
  "sales.priceLists.items",
  "sales.priceLists.discounts",
  "sales.priceLists.minAmount",
  "sales.priceLists.approvalThreshold",
  "sales.creditReview.title",
  "sales.creditReview.subtitle",
  "sales.creditReview.approveSelected",
  "sales.creditReview.list",
  "sales.creditReview.currentLimit",
  "sales.creditReview.requestedLimit",
  "sales.creditReview.openAr",
  "sales.commissions.title",
  "sales.commissions.subtitle",
  "sales.commissions.list",
  "sales.commissions.agent",
  "sales.commissions.earned",
  "sales.commissions.paid",
  "procurement.requests.title",
  "procurement.requests.subtitle",
  "procurement.requests.new",
  "procurement.requests.list",
  "procurement.requests.expected",
  "procurement.rfqs.title",
  "procurement.rfqs.subtitle",
  "procurement.rfqs.new",
  "procurement.rfqs.list",
  "procurement.rfqs.quotes",
  "procurement.rfqs.issued",
  "procurement.rfqs.deadline",
  "procurement.suppliers.subtitle",
  "procurement.suppliers.new",
  "procurement.suppliers.supplierNumber",
  "procurement.suppliers.openPayables",
  "procurement.evaluations.title",
  "procurement.evaluations.subtitle",
  "procurement.evaluations.list",
  "procurement.evaluations.onTime",
  "procurement.evaluations.quality",
  "procurement.evaluations.priceVariance",
  "procurement.evaluations.overall",
  "procurement.contracts.title",
  "procurement.contracts.subtitle",
  "procurement.contracts.new",
  "procurement.contracts.list",
  "procurement.contracts.start",
  "procurement.contracts.end",
  "procurement.contracts.linkedPos",
  "inventory.products.title",
  "inventory.products.subtitle",
  "inventory.products.new",
  "inventory.products.list",
  "inventory.products.valuation",
  "inventory.products.barcode",
  "inventory.products.variants",
  "inventory.products.typeOptions.Item",
  "inventory.products.typeOptions.Service",
  "inventory.products.typeOptions.Expense",
  "inventory.stock.title",
  "inventory.stock.subtitle",
  "inventory.stock.allWarehouses",
  "inventory.stock.list",
  "inventory.movements.title",
  "inventory.movements.subtitle",
  "inventory.movements.new",
  "inventory.movements.list",
  "inventory.movements.value",
  "inventory.movements.posted",
  "inventory.transfers.title",
  "inventory.transfers.subtitle",
  "inventory.transfers.new",
  "inventory.transfers.list",
  "inventory.transfers.shipped",
  "inventory.adjustments.title",
  "inventory.adjustments.subtitle",
  "inventory.adjustments.new",
  "inventory.adjustments.awaiting",
  "inventory.adjustments.exceeds",
  "inventory.adjustments.review",
  "inventory.adjustments.list",
  "inventory.adjustments.delta",
  "inventory.cycleCounts.title",
  "inventory.cycleCounts.subtitle",
  "inventory.cycleCounts.start",
  "inventory.cycleCounts.list",
  "inventory.cycleCounts.system",
  "inventory.cycleCounts.counted",
  "inventory.cycleCounts.variance",
  "inventory.cycleCounts.countedBy",
  "inventory.traceability.title",
  "inventory.traceability.subtitle",
  "inventory.traceability.searchPlaceholder",
  "inventory.traceability.searchLabel",
  "inventory.traceability.list",
  "inventory.traceability.quality",
  "inventory.reorder.title",
  "inventory.reorder.subtitle",
  "inventory.reorder.generate",
  "inventory.reorder.belowTitle",
  "inventory.reorder.below",
  "inventory.reorder.belowFix",
  "inventory.reorder.list",
  "inventory.reorder.min",
  "inventory.reorder.max",
  "inventory.reorder.suggested",
  "inventory.reorder.plannedPo",
  "inventory.reorder.exists",
  "inventory.reorder.missing",
  "inventory.valuation.title",
  "inventory.valuation.subtitle",
  "inventory.valuation.nowOnly",
  "inventory.valuation.totalAllPages",
  "inventory.valuation.list",
  "inventory.valuation.method",
  "inventory.valuation.unitCost",
  "inventory.valuation.totalValue",
  "admin.wizard.title",
  "admin.wizard.subtitle",
  "admin.wizard.empty",
  "admin.wizard.phases",
  "admin.wizard.step",
  "admin.wizard.blocked",
  "admin.wizard.phaseTitle",
  "admin.wizard.whyBlocked",
  "admin.wizard.phaseNames.Organization",
  "admin.wizard.phaseNames.Finance",
  "admin.wizard.phaseNames.Tax",
  "admin.wizard.phaseNames.UsersRoles",
  "admin.wizard.phaseNames.Approvals",
  "admin.wizard.phaseNames.MasterData",
  "admin.wizard.phaseNames.OpeningBalances",
  "admin.wizard.phaseNames.Integrations",
  "admin.wizard.phaseNames.Testing",
  "admin.wizard.phaseNames.GoLive",
  "admin.wizard.notices.SETUP_NO_COMPANY",
  "admin.wizard.notices.SETUP_NO_ACCOUNTS",
  "admin.wizard.notices.SETUP_NO_TAX_CODES",
  "admin.wizard.notices.SETUP_NO_MEMBERS",
  "admin.wizard.notices.SETUP_NO_APPROVAL_MATRIX",
  "admin.wizard.notices.SETUP_NO_PRODUCTS",
  "admin.wizard.notices.SETUP_NO_OPENING_ENTRIES",
  "admin.wizard.notices.SETUP_NO_INTEGRATIONS",
  "admin.wizard.notices.SETUP_NO_TEST_TRANSACTIONS",
  "admin.wizard.notices.SETUP_NOT_LIVE",
  "admin.wizard.notices.SETUP_WAITING_ON",
  "admin.wizard.notices.SETUP_PHASE_COMPLETE",
  "admin.auditLog.subtitle",
  "admin.auditLog.eventFilter",
  "admin.auditLog.allSeverities",
  "admin.auditLog.when",
  "admin.auditLog.actorType",
  "admin.auditLog.target",
  "admin.auditLog.targetId",
  "admin.apiWebhooks.title",
  "admin.apiWebhooks.subtitle",
  "admin.apiWebhooks.newClient",
  "admin.apiWebhooks.tabs.clients",
  "admin.apiWebhooks.tabs.webhooks",
  "admin.apiWebhooks.tabs.deadLetter",
  "admin.apiWebhooks.clientId",
  "admin.apiWebhooks.scopes",
  "admin.apiWebhooks.lastUsed",
  "admin.apiWebhooks.webhooksTitle",
  "admin.apiWebhooks.endpoint",
  "admin.apiWebhooks.events",
  "admin.apiWebhooks.paused",
  "admin.apiWebhooks.lastDelivery",
  "admin.apiWebhooks.failures",
  "admin.apiWebhooks.deadLetterTitle",
  "admin.apiWebhooks.topic",
  "admin.apiWebhooks.eventId",
  "admin.apiWebhooks.attempts",
  "admin.apiWebhooks.nextAttempt",
  "admin.users.title",
  "admin.users.subtitle",
  "admin.users.invite",
  "admin.users.list",
  "admin.users.on",
  "admin.users.off",
  "admin.users.roles",
  "admin.users.lastLogin",
  "admin.users.cancelInvitation",
  "admin.users.suspend",
  "admin.users.activate",
  "admin.roles.title",
  "admin.roles.subtitle",
  "admin.roles.create",
  "admin.roles.list",
  "admin.roles.system",
  "admin.roles.custom",
  "admin.roles.permissions",
  "admin.roles.members",
  "admin.roles.sodConflicts",
  "admin.workflows.title",
  "admin.workflows.subtitle",
  "admin.workflows.validate",
  "admin.workflows.simulate",
  "admin.workflows.publish",
  "admin.workflows.states",
  "admin.workflows.versionBadge",
  "admin.workflows.initial",
  "admin.workflows.final",
  "admin.workflows.transitions",
  "admin.workflows.auto",
  "admin.approvalMatrix.title",
  "admin.approvalMatrix.subtitle",
  "admin.approvalMatrix.validate",
  "admin.approvalMatrix.activate",
  "admin.approvalMatrix.unnamed",
  "admin.approvalMatrix.entity",
  "admin.approvalMatrix.roleStep",
  "admin.approvalMatrix.counterSignatures",
  "admin.approvalMatrix.escalation",
  "admin.approvalMatrix.afterHours",
  "admin.approvalMatrix.escalateTo",
  "admin.approvalMatrix.slaBreach",
  "admin.entityBuilder.title",
  "admin.entityBuilder.subtitle",
  "admin.entityBuilder.validateActivate",
  "admin.entityBuilder.newEntity",
  "admin.entityBuilder.entityName",
  "admin.entityBuilder.entityNameHint",
  "admin.entityBuilder.whyBlocked",
  "admin.entityBuilder.invalidName",
  "admin.entityBuilder.reservedName",
  "admin.entityBuilder.fieldTypes",
  "admin.entityBuilder.list",
  "admin.entityBuilder.storage",
  "admin.entityBuilder.fields",
  "admin.formDesigner.title",
  "admin.formDesigner.subtitle",
  "admin.formDesigner.tabs.views",
  "admin.formDesigner.tabs.layouts",
  "admin.formDesigner.viewsTitle",
  "admin.formDesigner.view",
  "admin.formDesigner.default",
  "admin.formDesigner.layoutsTitle",
  "admin.formDesigner.layout",
  "admin.countryPacks.title",
  "admin.countryPacks.subtitle",
  "admin.countryPacks.list",
  "admin.countryPacks.pack",
  "admin.countryPacks.packName",
  "admin.countryPacks.installed",
  "admin.countryPacks.latest",
  "admin.countryPacks.update",
  "admin.countryPacks.available",
  "admin.countryPacks.current",
  "admin.countryPacks.install",
  "admin.countryPacks.upgrade",
  "admin.countryPacks.disable",
  // FIX-CERT: the pack certification label and its explanation, written by
  // engineering (Decision 230's wording) for ux-writer review.
  "admin.countryPacks.review",
  "admin.countryPacks.reviewState.Default",
  "admin.countryPacks.reviewState.Certified",
  "admin.countryPacks.reviewUnknown",
  "admin.countryPacks.explain.title",
  "admin.countryPacks.explain.status",
  "admin.countryPacks.explain.Default",
  "admin.countryPacks.explain.Certified",
  "admin.countryPacks.explain.reviewer",
  "admin.countryPacks.explain.notReviewed",
  "admin.countryPacks.explain.date",
  "admin.countryPacks.explain.notRecorded",
  "admin.countryPacks.explain.sources",
  "admin.countryPacks.explain.noSources",
  "admin.countryPacks.explain.responsibility",
  "admin.countryPacks.explain.responsibilityText",
  "admin.countryPacks.explain.close",
  "admin.countryPacks.explain.parts.accounting",
  "admin.countryPacks.explain.parts.accounting_costing",
  "admin.countryPacks.explain.parts.agingBuckets",
  "admin.countryPacks.explain.parts.payrollAccounting",
  "admin.countryPacks.explain.parts.tax",
  "admin.countryPacks.explain.words.uncertified",
  "admin.countryPacks.explain.words.certified",
  "admin.countryPacks.explain.words.standard-based",
  "admin.industryPacks.title",
  "admin.industryPacks.subtitle",
  "admin.industryPacks.browse",
  "admin.industryPacks.list",
  "admin.industryPacks.pack",
  "admin.industryPacks.entities",
  "admin.industryPacks.workflows",
  "admin.industryPacks.notInstalled",
  "admin.industryPacks.install",
  "admin.industryPacks.manage",
  "admin.integrations.title",
  "admin.integrations.subtitle",
  "admin.integrations.add",
  "admin.integrations.list",
  "admin.integrations.connector",
  "admin.integrations.successRate",
  "admin.integrations.lastRun",
  "admin.integrations.configure",
  "admin.integrations.settings",
  "admin.dataImport.title",
  "admin.dataImport.subtitle",
  "admin.dataImport.template",
  "admin.dataImport.new",
  "admin.dataImport.list",
  "admin.dataImport.rows",
  "admin.dataImport.success",
  "admin.dataImport.errors",
  "admin.dataImport.started",
  "admin.dataImport.open",
  "admin.tenant.title",
  "admin.tenant.subtitle",
  "admin.tenant.save",
  "admin.tenant.environment",
  "admin.tenant.status",
  "admin.tenant.plan",
  "admin.tenant.companyName",
  "admin.tenant.tenantCode",
  "admin.tenant.localization",
  "admin.tenant.locale",
  "admin.tenant.region",
  "admin.tenant.isolation",
  "admin.tenant.backup",
  "admin.tenant.exportAll",
  "admin.security.title",
  "admin.security.subtitle",
  "admin.security.tabs.sessions",
  "admin.security.events",
  "admin.security.time",
  "admin.security.sessionsTitle",
  "admin.security.revokeAll",
  "admin.security.thisSession",
  "admin.security.revoke",
  "admin.billing.title",
  "admin.billing.subtitle",
  "admin.billing.compare",
  "admin.billing.plan",
  "admin.billing.users",
  "admin.billing.apiRequests",
  "admin.billing.paymentMethod",
  "admin.billing.none",
  "admin.billing.invoices",
];
